import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Seat } from '@annual-party/shared';

interface UserState {
  name: string | null;
  seat: Seat | null;
}

interface UserContextValue extends UserState {
  isAuthenticated: boolean;
  login: (name: string, seat: Seat) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

const STORAGE_KEY = 'user_session';

interface StoredSession {
  name: string;
  seat: Seat;
  loginAt: string;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserState>({ name: null, seat: null });

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const session: StoredSession = JSON.parse(stored);
        setUser({ name: session.name, seat: session.seat });
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const login = (name: string, seat: Seat) => {
    const session: StoredSession = {
      name,
      seat,
      loginAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setUser({ name, seat });
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setUser({ name: null, seat: null });
  };

  const value: UserContextValue = {
    name: user.name,
    seat: user.seat,
    isAuthenticated: !!user.name,
    login,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
